/**
 * Quadro do Mercado Livre (redesign admin, Fase 3). Puro e sem I/O.
 *
 * Não existe (ainda) um estado "de marketplace" separado: o pedido do ML é um
 * pedido unificado com `origin: "marketplace"` e segue a MESMA máquina de
 * estados dos demais canais. As colunas do quadro são só um agrupamento de
 * apresentação desses estados — mover um cartão é uma transição normal do
 * pedido (feita no detalhe), nunca um estado paralelo que divergiria da fila
 * de Pedidos.
 *
 * Mapa coluna ← estado unificado:
 *   Novos                  ← payment_pending
 *   Aguardando confirmação ← paid
 *   Em separação           ← separation
 *   Enviados               ← shipping, invoiced
 *   Concluídos (48 h)      ← delivered, só os atualizados nas últimas 48 h
 * `cancelled`/`returned` ficam fora do quadro e são contados à parte.
 *
 * A reconciliação Anúncios × ERP da referência exige o catálogo de anúncios do
 * ML, que a Plataforma não tem: a tela declara a fonte ausente em vez de
 * inventar o número.
 */
import type { PlatformUnifiedOrder } from "../api/types.js";
import { isRevenueState, type PlatformOrderState } from "./state-machine.js";
import { salesChannelKey } from "./channels.js";

export const MARKETPLACE_COLUMNS = [
  { id: "novos", title: "Novos", hint: "Aguardando pagamento", states: ["payment_pending"] },
  { id: "confirmacao", title: "Aguardando confirmação", hint: "Pagos, prontos para confirmar", states: ["paid"] },
  { id: "separacao", title: "Em separação", hint: "No estoque", states: ["separation"] },
  { id: "enviados", title: "Enviados", hint: "Expedidos ou faturados", states: ["shipping", "invoiced"] },
  { id: "concluidos", title: "Concluídos (48 h)", hint: "Entregues nas últimas 48 h", states: ["delivered"] }
] as const satisfies ReadonlyArray<{ id: string; title: string; hint: string; states: readonly PlatformOrderState[] }>;

export type MarketplaceColumnId = (typeof MARKETPLACE_COLUMNS)[number]["id"];

/** Janela de exibição dos concluídos: depois dela o pedido sai do quadro (continua em Pedidos). */
export const COMPLETED_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Pedidos do canal. Sem `channelKey` valem todos os de origem `marketplace` (comportamento
 * original). Com `channelKey` (ex.: `shopee`) vale o canal de venda real do Bling; o Mercado
 * Livre também aceita pedido de marketplace sem canal informado (registros anteriores ao Bling).
 */
export function marketplaceOrders(orders: PlatformUnifiedOrder[], channelKey?: string): PlatformUnifiedOrder[] {
  if (!channelKey) return orders.filter((order) => order.origin === "marketplace");
  return orders.filter(
    (order) =>
      salesChannelKey(order) === channelKey ||
      (channelKey === "mercado_livre" && order.origin === "marketplace" && !order.channelKind)
  );
}

export interface MarketplaceColumn {
  id: MarketplaceColumnId;
  title: string;
  hint: string;
  orders: PlatformUnifiedOrder[];
}

export function marketplaceColumns(orders: PlatformUnifiedOrder[], now: Date, channelKey?: string): MarketplaceColumn[] {
  const rows = marketplaceOrders(orders, channelKey);
  return MARKETPLACE_COLUMNS.map((column) => {
    const states = column.states as readonly PlatformOrderState[];
    const inColumn = rows.filter((order) => {
      if (!states.includes(order.state)) return false;
      if (column.id !== "concluidos") return true;
      // `updatedAt` inválido não pode esconder a venda concluída: na dúvida, mostra.
      const updated = Date.parse(order.updatedAt);
      return Number.isNaN(updated) || now.getTime() - updated <= COMPLETED_WINDOW_MS;
    });
    return {
      id: column.id,
      title: column.title,
      hint: column.hint,
      orders: [...inColumn].sort((a, b) => b.placedAt.localeCompare(a.placedAt))
    };
  });
}

export interface MarketplaceSummary {
  total: number;
  /** Da cobrança até o faturamento — fila de trabalho. */
  inProgress: number;
  /** Cancelados + devolvidos (fora do quadro). */
  undone: number;
  revenueCents: number;
}

export function marketplaceSummary(orders: PlatformUnifiedOrder[], channelKey?: string): MarketplaceSummary {
  const rows = marketplaceOrders(orders, channelKey);
  return {
    total: rows.length,
    inProgress: rows.filter((o) => !["delivered", "cancelled", "returned"].includes(o.state)).length,
    undone: rows.filter((o) => o.state === "cancelled" || o.state === "returned").length,
    revenueCents: rows.reduce((sum, o) => (isRevenueState(o.state) ? sum + (o.totalCents ?? 0) : sum), 0)
  };
}
